import { BrowserRouter as Router, Navigate, Routes, Route, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Navbar } from './components/layout/Navbar';
import { HomePage } from './pages/HomePage';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { DashboardPage } from './pages/DashboardPage';
import { ServerDetailsPage } from './pages/ServerDetailsPage';
import { AdminPage } from './pages/AdminPage';
import { CustomInstancesPage } from './pages/CustomInstancesPage';
import { CustomInstanceDetailPage } from './pages/CustomInstanceDetailPage';
import { GamesPage } from './pages/GamesPage';
import SSHWindowPage from './pages/SSHWindowPage';
import { OAuthCallbackPage } from './pages/OAuthCallbackPage';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { AuthProvider } from './contexts/AuthContext';
import { SocketProvider } from './contexts/SocketContext';
import { PUBLIC_PRELAUNCH } from './config/publicSite';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

function App() {
  // Inner component to access location inside Router
  function AppShell() {
    const location = useLocation();
    const isSSHWindow = location.pathname.startsWith('/ssh/');

    return (
      <div className={isSSHWindow ? 'bg-dark-950' : 'min-h-screen bg-dark-950'}>
        {!isSSHWindow && <Navbar />}
        <main>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={PUBLIC_PRELAUNCH ? <Navigate to="/" replace /> : <RegisterPage />} />
            <Route path="/oauth/callback" element={<OAuthCallbackPage />} />
            <Route
              path="/games"
              element={PUBLIC_PRELAUNCH ? (
                <ProtectedRoute requireAdmin>
                  <GamesPage />
                </ProtectedRoute>
              ) : <GamesPage />}
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute requireAdmin={PUBLIC_PRELAUNCH}>
                  <DashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/servers/:id"
              element={
                <ProtectedRoute requireAdmin={PUBLIC_PRELAUNCH}>
                  <ServerDetailsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/custom-instances"
              element={
                <ProtectedRoute requireAdmin>
                  <CustomInstancesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/custom-instances/:instanceId"
              element={
                <ProtectedRoute requireAdmin>
                  <CustomInstanceDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/ssh/:id"
              element={
                <ProtectedRoute requireAdmin>
                  <SSHWindowPage />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <SocketProvider>
          <Router>
            <AppShell />
          </Router>
        </SocketProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
